/**
 * Auth Recovery Utility
 * 
 * Provides emergency recovery mechanisms for auth loops and issues
 */

import { supabase } from '@/integrations/supabase/client';
import { clearAuthCache, clearAllCache } from './cacheHelper';
import { resetAuthRateLimit, getAuthRateLimitStatus } from './authRateLimiter';
import { clearAuthDebugger, getAuthStats } from './authDebugger';

interface AuthRecoveryOptions {
  clearCache?: boolean;
  resetRateLimit?: boolean;
  forceSignOut?: boolean;
  refreshPage?: boolean;
}

interface AuthDiagnostics {
  rateLimitStatus: ReturnType<typeof getAuthRateLimitStatus>;
  authStats: ReturnType<typeof getAuthStats>;
  sessionValid: boolean;
  hasMultipleListeners: boolean;
  recommendations: string[];
}

/**
 * Perform comprehensive auth diagnostics
 */
export const diagnoseAuthIssues = async (): Promise<AuthDiagnostics> => {
  const recommendations: string[] = [];
  
  // Check rate limit status
  const rateLimitStatus = getAuthRateLimitStatus();
  if (rateLimitStatus.isCircuitBreakerOpen) {
    recommendations.push('Auth rate limiter circuit breaker is open - wait for reset or force reset');
  }
  if (rateLimitStatus.recentOperations > 5) {
    recommendations.push('High auth activity detected - consider rate limiting');
  }
  
  // Check auth event stats
  const authStats = getAuthStats();
  if (authStats.recentEvents > 10) {
    recommendations.push('Too many recent auth events - potential loop detected');
  }
  
  // Check for TOKEN_REFRESHED loops
  if (authStats.eventTypes['TOKEN_REFRESHED'] && authStats.eventTypes['TOKEN_REFRESHED'] > 5) {
    recommendations.push('Excessive TOKEN_REFRESHED events - clear cache recommended');
  }
  
  // Check session validity
  let sessionValid = false;
  try {
    const { data, error } = await supabase.auth.getSession();
    sessionValid = !error && !!data.session;
  } catch (error) {
    recommendations.push('Session check failed - auth state corrupted');
  }
  
  // Check for multiple auth listeners (simplified check)
  const hasMultipleListeners = authStats.sources && Object.keys(authStats.sources).length > 2;
  if (hasMultipleListeners) {
    recommendations.push('Multiple auth listeners detected - may cause conflicts');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('No auth issues detected');
  }
  
  return {
    rateLimitStatus,
    authStats,
    sessionValid,
    hasMultipleListeners,
    recommendations
  };
};

/**
 * Perform emergency auth recovery
 */
export const performAuthRecovery = async (options: AuthRecoveryOptions = {}): Promise<void> => {
  console.log('Starting auth recovery process...');
  
  const {
    clearCache = true,
    resetRateLimit = true,
    forceSignOut = false,
    refreshPage = true
  } = options;
  
  try {
    // Step 1: Reset rate limiter
    if (resetRateLimit) {
      console.log('Resetting auth rate limiter');
      resetAuthRateLimit();
    }
    
    // Step 2: Clear auth debugger
    console.log('Clearing auth debugger');
    clearAuthDebugger();
    
    // Step 3: Force sign out if requested
    if (forceSignOut) {
      console.log('Force signing out user');
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (signOutError) {
        console.warn('Sign out failed:', signOutError);
      }
    }
    
    // Step 4: Clear cache
    if (clearCache) {
      console.log('Clearing auth cache');
      clearAuthCache();
    }
    
    // Step 5: Refresh page if requested
    if (refreshPage) {
      console.log('Refreshing page in 2 seconds...');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
    
    console.log('Auth recovery process completed');
  } catch (error) {
    console.error('Error during auth recovery:', error);
    
    // Last resort: clear everything and refresh
    console.log('Performing emergency recovery - clearing all cache');
    await clearAllCache();
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
};

/**
 * Quick auth recovery for common issues
 */
export const quickAuthRecovery = async (): Promise<void> => {
  console.log('Performing quick auth recovery');
  
  const diagnostics = await diagnoseAuthIssues();
  
  // Determine recovery strategy based on diagnostics
  if (diagnostics.rateLimitStatus.isCircuitBreakerOpen) {
    // Rate limited - just reset and wait
    await performAuthRecovery({
      clearCache: false,
      resetRateLimit: true,
      forceSignOut: false,
      refreshPage: false
    });
  } else if (diagnostics.authStats.recentEvents > 15) {
    // Auth loop detected - aggressive recovery
    await performAuthRecovery({
      clearCache: true,
      resetRateLimit: true,
      forceSignOut: true,
      refreshPage: true
    });
  } else if (!diagnostics.sessionValid) {
    // Session issues - sign out and clear cache
    await performAuthRecovery({
      clearCache: true,
      resetRateLimit: false,
      forceSignOut: true,
      refreshPage: true
    });
  } else {
    // Minor issues - just clear cache
    await performAuthRecovery({
      clearCache: true,
      resetRateLimit: true,
      forceSignOut: false,
      refreshPage: false
    });
  }
};

/**
 * Monitor for auth loops and auto-recover
 */
export const enableAuthMonitoring = (): () => void => {
  let monitoringInterval: NodeJS.Timeout;
  
  const checkForLoops = async () => {
    try {
      const diagnostics = await diagnoseAuthIssues();
      
      // Check for critical conditions
      const criticalConditions = [
        diagnostics.authStats.recentEvents > 20,
        diagnostics.rateLimitStatus.recentOperations > 8,
        (diagnostics.authStats.eventTypes['TOKEN_REFRESHED'] || 0) > 8
      ];
      
      if (criticalConditions.some(Boolean)) {
        console.warn('Critical auth condition detected, triggering recovery');
        clearInterval(monitoringInterval);
        await quickAuthRecovery();
      }
    } catch (error) {
      console.error('Error in auth monitoring:', error);
    }
  };
  
  // Check every 30 seconds
  monitoringInterval = setInterval(checkForLoops, 30000);
  
  console.log('Auth monitoring enabled');
  
  // Return cleanup function
  return () => {
    clearInterval(monitoringInterval);
    console.log('Auth monitoring disabled');
  };
};

// Make utilities available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).authRecovery = {
    diagnose: diagnoseAuthIssues,
    recover: performAuthRecovery,
    quickRecover: quickAuthRecovery,
    enableMonitoring: enableAuthMonitoring
  };
} 